import type { HourlyEntry } from '../types';

export interface MorningConditions {
  active: boolean;
  clearsAt: string | null;
  message: string | null;
}

// Rough heuristic: dew / fog is present when temp-dewpoint spread is small and
// RH is high; it clears when the spread opens up (sun warms, air dries).
// Only relevant for morning tee times — we short-circuit after ~11am local.
//
// Thresholds:
//   "Wet" hour: spread ≤ 4°F AND humidity ≥ 90%.
//   "Dry" hour: spread ≥ 5°F (or humidity drops below 80%).
export function detectMorningFog(
  hourly: HourlyEntry[],
  timeZone: string,
  now: Date = new Date(),
): MorningConditions {
  if (hourly.length === 0) return { active: false, clearsAt: null, message: null };

  const localHour = Number(
    new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false }).format(now),
  );
  if (localHour >= 11) return { active: false, clearsAt: null, message: null };

  const hasFog = (h: HourlyEntry) => {
    if (h.dewpointF == null) return false;
    const spread = h.tempF - h.dewpointF;
    return spread <= 4 && (h.humidity ?? 100) >= 90;
  };
  const isClear = (h: HourlyEntry) => {
    if (h.dewpointF == null) return true;
    const spread = h.tempF - h.dewpointF;
    return spread >= 5 || (h.humidity != null && h.humidity < 80);
  };

  // Check the next 3 hours for fog. If none, skip.
  const soon = hourly.slice(0, 4);
  if (!soon.some(hasFog)) return { active: false, clearsAt: null, message: null };

  // Find the first subsequent hour that's clearly dry.
  for (let i = 0; i < Math.min(12, hourly.length); i++) {
    if (isClear(hourly[i])) {
      const clearsAt = hourly[i].time;
      const timeStr = new Date(clearsAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      });
      return {
        active: true,
        clearsAt,
        message: `Morning dew/fog likely — greens dry around ${timeStr}.`,
      };
    }
  }
  return {
    active: true,
    clearsAt: null,
    message: 'Morning dew/fog likely — no clear drying time in the forecast.',
  };
}
